/**
 * Wire-format primitives + protocol-level constants for the
 * ArkheHoldem WS broadcast stream.
 *
 * The numeric `WIRE_FORMAT_MAJOR` / `WIRE_FORMAT_MINOR` pins the
 * client's wire-shape understanding. The protocol-version
 * dispatcher accepts any frame whose MAJOR matches and the
 * `KNOWN_WS_EVENT_KINDS` graceful-skip arm silently drops
 * unknown kinds, so MINOR bumps stay forward-compatible until
 * a structural change forces a MAJOR bump.
 *
 * Mirrors ADR-007 v0.6. The changelog block on `WIRE_FORMAT_MINOR`
 * carries the per-minor diff so a future bump only needs to extend
 * the trailing block rather than restate the lineage.
 *
 * ## Forbidden-field rule (ADR-007 §forbidden fields)
 *
 * The following per-seat secret / structural / internal-evaluator
 * fields are NEVER declared in any view type, so a property access
 * fails at the TS type level (`Property 'hole_cards' does not exist
 * on type ...`). Forbidden field list:
 *
 *   1. `hole_cards`              — per-seat 2-card hand
 *   2. `deck_state`              — full ordered deck post-shuffle
 *   3. `seed_contribution`       — per-player pre-reveal contribution
 *   4. `server_seed_pre_reveal`  — server seed in commit phase
 *   5. `hand_evaluation_intermediate` — showdown intermediate ranking
 *
 * `final_seed` is a distinct equivalence class for *seated players*
 * only (post-reveal public-derivable aggregate; see ADR-001 trust-
 * model disclaimer). For the spectator lane the v0.6 amend adds
 * `final_seed` to the forbidden list — observers must not be able
 * to re-derive any seat's hole cards. The spectator-lane variants
 * carry NO `final_seed` field at the type level, so a future edit
 * cannot accidentally leak the seed via either variant.
 */

export const WIRE_FORMAT_MAJOR = 2;
// Minor 9 corresponds to ADR-007 v1.3 — adds
// `HostOwnershipTransferred { transfer_seq, old_host_seat,
// new_host_seat, reason, server_unix_ms }` so every connected
// subscriber learns about host transitions (initial assignment,
// AFK timeout, busted seat) at the moment they fire, plus the
// `host_seat: number | null` additive field on `HandSnapshot`
// + `SpectatorHandSnapshot` so a reconnecting client can hydrate
// the current host without waiting for the next transfer.
//
// Host seat is *table-lifetime* (NOT per-hand), so the
// frontend reducer carries it through every fresh-hand boundary
// — applying the per-hand wipe pattern here would corrupt the
// state on the very first HandStarted after a transfer.
//
// Minor 8 (v1.1) shipped `TimerArmed` + `TimerExpired`; minor 7
// (v1.0) shipped `Settled.invested`; minor 6 (v0.9) shipped
// `HandShowdown`; minor 5 (v0.8) shipped `NextHandStarted`;
// minor 4 (v0.7) shipped `PlayerJoined`; minor 3 (v0.6) shipped
// the spectator wire surface.
// Wire minor 10 added `invite_code` + `TableSummary` meta on the
// `GET /tables` lobby surface; the bump from 9 → 11 once carried
// the pending-join queue (three WsEvent variants +
// `pending_join` on both HandSnapshot surfaces), all of which the
// BE stopped emitting and the FE has since retired — the
// unknown-kind graceful skip in `KNOWN_WS_EVENT_KINDS` keeps any
// stray pre-retirement frame parse-safe.
//
// Wire minor 12 (ADR-007 v1.6) — server-enforced WS-lane
// authentication. The handshake gains 3 inbound variants
// (`auth_challenge` / `auth_ok` / `auth_fail`) and the client
// gains one outbound command (`auth_response`). The 5-frame
// dance is ProtocolVersion → AuthChallenge → AuthResponse (client)
// → AuthOk | AuthFail → Snapshot (replay). 6 HTTP routes also
// require the `x-player-id` / `x-player-sig` / `x-request-ts`
// header triple — see `src/session/auth_headers.ts`. A pre-bump
// client that never sends `AuthResponse` falls through the 30 s
// nonce TTL and lands on AuthFail → reload prompt.
//
// Wire minor 18 — seed-protocol observability quartet
// (`seed_round_opened` / `seed_contrib_landed` / `seed_reveal_ready`
// / `seed_finalized`) emitted from the BE seed handlers so the FE
// orchestrator replaces its 4 HTTP-retry polling loops with reactive
// phase transitions. `Snapshot` + `SpectatorSnapshot` gain a
// `seed_in_flight: SeedRoundView | null` companion field so a
// reconnecting client that missed `SeedRoundOpened` rehydrates the
// canonical round state in the same frame the rest of the table
// re-syncs. Spectator-lane carry is identity (seed-round liveness
// is public table state — no per-seat secret enters any payload).
// The FE jumps directly from 12 → 18; intermediate minors 13–17
// shipped variants the FE either ignores via graceful-skip
// (`hand_error` / `displaced`) or already absorbed structurally
// (`min_raise`, `player_left`, `leave_queued`). The version
// dispatcher only enforces MAJOR parity, so the leapfrog is safe.
//
// Wire minor 19 — stale-seed sweep. The BE adds a periodic sweep
// job that aborts an in-flight seed round when one or more
// expected contributors never POST their envelope within the
// configured threshold window; the new `seed_round_aborted`
// variant carries the abort reason + the player_ids that missed
// the window. `SeedRoundView` gains `opened_at_ms` so a
// reconnecting client can render the round's age as a UX cue.
//
// Wire minor 20 — seed-contrib envelope commit-binding. The
// `sign_payload_seed_contrib` BLAKE3 digest now appends the
// 32-byte server `commit` after the contribution bytes; the
// envelope wire shape itself is unchanged (commit is reconstructed
// by the BE from its own `SeedRoundSnapshot`). A pre-bump server
// that doesn't append the commit will reject any post-bump
// signature as `seed.bad_signature` — forced upgrade, no
// compatibility shim.
//
// Wire minor 21 — `TableDissolved` lifecycle frame. Every
// table-cleanup path (route-side last-player leave / timer-driven
// stale-empty sweep / admin force-kill) now publishes
// `WsEvent::TableDissolved` before the per-table broadcast sender
// drops, so the FE receives an authoritative "table vanished" cue
// rather than inferring it from a Lagged / Closed socket. The
// `cause` tagged-enum tells the dismiss modal why it's rendering
// (`lived_then_vacated` / `creator_abandon` /
// `admin_force_dissolve { reason }`) so the user-facing copy
// matches the actual lifecycle path; `dissolved_at_ms` carries
// the BE-side wall-clock so a client that receives the frame
// after a socket-buffer delay renders an absolute timestamp
// instead of a misleading "방금" tag. Pre-bump clients ignore
// the new variant via the graceful-skip dispatch in
// `parse.ts::parseWsFrame`.
//
// Wire minor 22 — `PlayerKicked` operator-action frame. Admin
// API `POST /admin/tables/:id/kick` force-removes a single seat
// (distinct from the whole-table dissolve path). The variant
// carries the seat_idx + player_id + admin_account_id + reason
// + kicked_at_ms so the kicked user's modal renders the
// operator note verbatim and other seated players can show a
// "{seat} kicked by operator: {reason}" action-log line. Mid-
// hand kick funnels through a synthetic Fold on the BE before
// the seat vacates, so the FE side just needs the wire reducer
// + the per-user modal; the action-log injection already runs
// off the resulting `ActionApplied` frame. Pre-bump clients
// ignore the variant via the same graceful-skip dispatch.
//
// Wire minor 23 — user-driven `SeatSitOutFlipped` frame. The
// `POST /tables/:id/seat/sit-out` + `/sit-back` endpoints let
// a seated player flip their own SittingOut state; the BE
// broadcasts the new variant + the `queued` discriminator
// (immediate vs next-hand-boundary apply) so every subscriber
// updates the seat HUD badge consistently. Pre-bump clients
// ignore the variant via the same graceful-skip dispatch.
//
// Wire minor 24 — leave-unification. The BE drops the legacy
// `LeaveQueued` intermediate broadcast (every `/leave` now
// applies immediately, mid-hand or off-hand) and adds
// `forfeit_reason?: "voluntary"` to `PlayerLeft` so a mid-hand-
// fold leave renders the "포기 후 퇴장" cue while a regular
// inter-hand leave keeps the generic "퇴장" surface. The legacy
// `/tables/:id/leave/cancel` HTTP route was kept as a 200-no-op
// for pre-bump callers, and has since been removed. Pre-bump clients
// ignore the new field via structural-typing optionality.
//
// Wire minor 25 — chip-economy buy-in floor surfacing
// (`CHIP_ECONOMY_AND_LEAVE_POLICY` §1). The BE adds a
// `min_buy_in: u64` field on the `GET /tables/:id/state`
// response so the FE renders the join-modal slider floor from
// server state (no magic constant). The matching POST
// `/tables/:id/join` + `/join-queue` routes enforce the same
// floor server-side and reject sub-floor buy-ins with
// HTTP 400 + `error.buy_in_below_minimum`. Pre-bump FE clients
// ignore the new field via JSON unknown-key skip; post-bump
// clients prefer the server value over a hard-coded constant
// so a future per-mode override surfaces without a wire-shape
// change. No new event variants — pure additive field.
//
// Wire minor 26 — table chat broadcast lane. The BE adds a
// `POST /tables/:id/chat` route (seated-player-only, 2 s
// per-sender throttle, 200-char cap, URL strip / whitespace
// collapse via the engine sanitiser) that publishes a new
// `WsEvent::ChatBroadcast` variant on every accepted send.
// The variant carries `(seat_idx, player_id, username?,
// message, sent_at_ms)` so the FE chat panel renders each
// row with the same identity affordances the action log
// already exposes. Four canonical error keys surface on the
// reject path: `error.chat_message_empty` /
// `error.chat_message_too_long` (sanitiser),
// `error.chat_rate_limited` (per-sender throttle), and
// `table.player_not_seated` (seated-only gate). Pre-bump
// clients ignore the variant via the unknown-kind graceful
// skip; pre-bump senders never POST `/chat` so the route
// stays inert from their side.
//
// Wire minor 27 — auto-deal ETA on the reconnect snapshot
// (RETIRED). The field `next_hand_eta_unix_ms` was removed from
// the BE `WsEvent::Snapshot` / `SpectatorSnapshot` envelopes and
// dropped from the FE wire types — neither side carries it. The
// FE renders a local-wall-clock inter-hand countdown, so no
// server-supplied ETA is needed. No replacement field.
//
// Wire minor 28 — `HandError` + `Displaced` variants formally
// adopted by the TS client. The BE shipped both arms several
// minors earlier (the leapfrog 12 → 18 changelog note above
// already listed them as graceful-skip placeholders); this
// bump records the FE crossing them off the placeholder list:
//   - `HandError { hand_seq, reason }` — engine rejected a
//     post-resolve frame (e.g. settle-invariant breach). The
//     reducer stamps `last_hand_error = { hand_seq, reason }`
//     so an App-level toast effect surfaces the stable
//     `reason` token through `lib/server_error.ts`
//     `mapServerErrorToToast` (Korean copy per invariant
//     class). State otherwise untouched — the BE recovers the
//     table on its own via the next `NextHandStarted` /
//     `HandStarted` cue.
//   - `Displaced { reason }` — server-initiated socket
//     teardown (a newer authed session claimed the
//     `(table_id, player_id)` singleton slot). The reducer
//     records `connection.disconnect = surface_displaced` so the
//     WS client's on-close handler stops the reconnect loop +
//     the Game-level router halts this surface (take-over notice)
//     WITHOUT a logout. `markReconnected` restores it to `live`.
// Pre-bump clients drop both variants via the unknown-kind
// graceful-skip in `KNOWN_WS_EVENT_KINDS`; the bump only
// signals that the FE now carries dedicated reducers.
//
// Wire minor 29 / 30 — min-client-version gate + single-active-
// surface (user-approved policies, 2026-06-02). Both minors are
// purely additive on the wire, so the FE crosses them off together:
//   - minor 29 added the `surface?: string` token on `auth_ok` — the
//     opaque single-active-surface id the client echoes back on every
//     mutating table-scoped HTTP request via `x-surface-id`. Optional
//     (a pre-policy server omits it → the HTTP gate stays allow-by-
//     default), so the field is tolerated structurally with no parse
//     change.
//   - minor 30 added the outbound `client_major` field on the
//     `auth_response` ClientCommand — the protocol MAJOR this build
//     speaks (= `WIRE_FORMAT_MAJOR`). The BE's `ws_major_requires_upgrade`
//     closes the socket with `WS_CLOSE_UPGRADE_REQUIRED` (4001) when the
//     declared major mismatches its own; the encoder
//     (`ws/parse.ts::encClientCommand`) always stamps it so the upgrade
//     close can actually fire on a major skew.
// Wire minor 31 — additive seat-owner roster carry (BE-led: the
// per-seat `owner` END-field on `SeatStartedView` / `SeatSnapshotView`
// + the top-level `occupied_seats` END-field on the `Snapshot` /
// `SpectatorSnapshot` variants). The FE tolerated it structurally
// (graceful-skip / HTTP-hydrated roster authoritative), so this build
// crosses it off while aligning the stale 30→31 skew.
//
// Wire minor 32 — per-request CSPRNG nonce on the HTTP auth canonical
// message. The three header families (`signPlayerAuthHeaders` /
// `signAccountSelfAuthHeaders` + the state-query read) draw 16 fresh
// CSPRNG bytes per call, fold them into the BLAKE3 signing payload as
// its FINAL field, and emit `x-request-nonce: <hex>`. Every legitimate
// request now signs structurally-distinct bytes, so the prior
// monotonic-clock / per-tab-body-nonce / replay-retry scaffolding is
// removed. Dual-accept on the BE: a stale client that omits the header
// lands on the byte-identical legacy reconstruction branch, so the bump
// never locks anyone out (the gate is `>=` against the `(2, 0)` floor).
// HTTP-auth-payload + header only — no WsEvent/WalEvent enum body
// touched.
// Both ends now report `(2, 32)`: the BE `WIRE_FORMAT_MINOR` = 32 and
// `MIN_CLIENT_MINOR` = 0, so the same-wire-version invariant holds and
// the current build sits comfortably above the agreed `(2, 0)` floor.
//
// Wire minor 33 — per-caller `my_next_seq` action-seq mirror on the
// player-lane `HandSnapshot`. The server surfaces its authoritative
// `HandActionWindow.next_seq(caller)` (the count+1 of the AUTHED caller's
// own accepted actions this hand) as a trailing `Option<u64>` on the WS
// authed Snapshot's hand view. The reducer mirrors it on rehydrate so a
// mid-hand reload sets `my_action_seq = my_next_seq - 1` instead of
// re-initing to 0 and wedging on a permanent 409 DuplicateOrOldSequence
// rollback loop (the server window is never reset — the replay
// double-debit stays closed). The field is a trailing nullable on the
// `HandSnapshot` STRUCT, not the WsEvent enum body (which references the
// type by name only), so the append-only enum baseline is byte-identical
// — no postcard/baseline regen. Dual-accept: a pre-bump server omits the
// field and the reducer keeps the existing seq (legacy behavior), and a
// pre-bump client ignores the field via structural typing — no lockout.
// Player lane only — never on the spectator `SpectatorHandSnapshot`.
// Both ends now report `(2, 33)`.
//
// Wire minor 34 — `dealt_seats` on the LIVE `hand_started` +
// `spectator_hand_started` variants. The BE carries the frozen
// `deal_eligible` roster (sorted ascending) on the hand-start frame,
// sourced from the IDENTICAL phase-frozen `InHand.dealt_seats` value
// `HandSnapshot.dealt_seats` already emits — so the live hand-start
// frame and the reconnect Snapshot carry the same deal roster and
// cannot diverge. The hand_lifecycle reducer now reads
// `HandStarted.dealt_seats` for the deal-cursor / hero-hole-card index
// mapping (matching how it consumes `HandSnapshot.dealt_seats`), NOT
// the over-inclusive occupied `seats` set: an occupied SittingOut /
// zero-stack seat present at hand start would otherwise shift every
// hero's hole-card index. Additive END-of-variant append on EXISTING
// variants — the postcard variant order is preserved, so the BE
// append-only baseline regen is a sanctioned additive-field regen, not
// a tag shift. Dual-accept both skew directions: a pre-bump server
// omits the field and the reducer falls back to the occupied set; a
// pre-bump client ignores the field via structural typing — no
// lockout. Public table state — rides the spectator lane identically
// but inert there (no `final_seed`).
//
// Wire minor 35 — `Halted` lobby-status. The BE adds a `Halted` arm to
// the JSON lobby-status enum (`TableStatus`, surfaced on `GET /tables`)
// so a table parked on the typed terminal `TablePhase::Halted` (an
// invariant-breach emergency stop) reads as distinctly halted +
// NON-JOINABLE rather than as a normal idle / between-hands table.
// `TableStatus` is a JSON enum the FE lobby consumes, NOT part of the
// postcard `WsEvent` append-only baseline, so the enum SHA is unchanged.
// Additive + dual-accept: a pre-bump FE that does not know `halted`
// degrades gracefully — the value never lands in the joinable buckets
// and the status label falls back rather than crashing. No lockout. The
// FE lobby consumes the field today.
//
// Wire minor 36 — per-caller `my_legal_actions: LegalActionSetView |
// null` trailing field on the player-lane `HandSnapshot` (the WS authed
// Snapshot's hand view) plus the `legal` field on the `POST /action`
// response. Both carry the engine `HandState::legal_actions` authority's
// `LegalActionSet` for the AUTHED caller's OWN seat — the SINGLE bounds
// source the `apply_*` validators also read, so the offered Call / Bet /
// Raise bounds can never diverge from what the engine accepts. Surfaced
// ONLY when it is that caller's turn: the reconnect Snapshot path seeds
// the action bar, the action-response path keeps the re-acting client
// fresh without a reconnect. Per-caller-private — same privacy class as
// the caller's own hole cards, NEVER broadcast, NEVER on the spectator
// lane. The snapshot field is a trailing `Option` on the `HandSnapshot`
// STRUCT, NOT the `WsEvent` enum body, so the append-only enum baseline
// is byte-identical. `null` / absent on the spectator lane / off-turn /
// between hands / for a pre-bump server. The FE now consumes it as the
// SOLE legality source (`use_action_legality` + `ActionBar` render
// against it directly; the prior client-side re-derivation is deleted).
// Both ends now report `(2, 36)`.
//
// Wire minor 37 — value-semantics correction, NO shape change. A
// showdown LOSER that auto-mucks now arrives with `HandShowdown
// .seats[i].hole_cards = null` (previously a non-null pair — the BE
// leaked a losing seat's cards on the spectator lane that the
// `Settled.mucked_seats` set already concealed). The BE now projects
// both `HandShowdown.hole_cards` and `Settled.mucked_seats` from a
// single engine showdown-disclosure authority, so the two can no
// longer diverge. No FE code change: `hole_cards` is already
// `readonly [CardView, CardView] | null` and the muck label already
// renders off `mucked_seats`. Both ends now report `(2, 37)`.
//
// Wire minor 38 — additive `hand_seq` on the `Snapshot` /
// `SpectatorSnapshot` frames (the table's current hand number, carried
// at the top level so it survives the `hand: null` inter-hand pause).
// Fixes the seed-round strand where a late joiner / reconnect landing
// between hands reset `hand_seq` to 0, targeted the wrong seed round,
// never contributed, and got swept to sit-out. Pure additive
// (`#[serde(default)]` on the BE, optional on the FE), so a pre-bump
// peer omits it and the reducer derives it from the in-flight round.
// Both ends now report `(2, 38)`.
//
// Wire minor 39 — four additive fields serving one change: the felt
// draws a FIXED chair ring, so a chair's position stops being a
// function of who is currently sitting
// (`docs/architecture/SEAT_RING_GEOMETRY.md`).
//   - `Snapshot` / `SpectatorSnapshot` gain `max_seats` (the table's
//     chair COUNT). Geometry needs it on the FIRST frame; sourcing it
//     from the one-shot `GET /tables/:id/state` read left a window
//     where the seat map had landed and the chair count had not, and
//     re-rotated the whole ring when the read returned.
//   - `player_joined` gains `stack` + `sitting_out`, so the reducer can
//     write a real SEAT row. Without them a player who sat down
//     mid-hand stayed drawn as an OPEN SEAT on every other client
//     until the next `HandStarted`.
//   - `SeatSnapshotView` gains `waiting_first_hand`, separating an
//     ARRIVING seat from an ABSENT one (both are `sitting_out`) so a
//     reconnect into the same hand still labels the newcomer as
//     joining rather than as having stepped away.
// All four are optional / `#[serde(default)]`, so a pre-bump peer
// omits them and the FE falls back to the HTTP chair count and the
// roster-only join behavior. Both ends now report `(2, 39)`.
// Wire minor 40 — `stack` on the `Snapshot` / `SpectatorSnapshot`
// occupancy roster. That roster is the only membership source between
// hands, so without a stack the felt showed every seat holding nothing
// until the first deal. Additive; a pre-bump server omits it and the
// client reads 0. Both ends now report `(2, 40)`.
// Wire minor 41 — `AllInTabled`. Hands are turned face up the moment
// no further betting is possible (the `AllAllIn` round close) and the
// board then runs out under them, which is what every room does and
// the only order in which the runout can be read. Distinct from
// `HandShowdown`, which lands at SETTLE and applies the auto-muck
// policy; nobody has won yet at tabling time, so every seat still in
// the hand is shown and `hand_rank` is empty until the board is
// complete. Additive END-of-enum variant — a pre-bump client skips it
// via the unknown-variant rule and sees the pre-41 behavior (cards
// only at settle). Both ends now report `(2, 41)`.
// Wire minor 42 — `HandShowdownSeat.hand_rank_ranks`, the ranks a hand
// is NAMED by (most significant first, face values). `hand_rank` is the
// category and only the category, so a client holding just that prints
// "트리플" where the hand was "5 트리플". Sent as DATA rather than a
// phrase: the server owns the evaluation (it is what the pot was
// awarded on) and the client owns the wording, which is localisation.
// The alternative — the client re-evaluating the revealed cards — puts
// a second evaluator in the system and needs a rule for disagreement.
// Additive; a pre-bump server omits it and the client falls back to the
// bare category label.
//
// Minor 43 adds `sitting_out` + `waiting_first_hand` to
// `SeatOwnerSlot`, finishing what minor 40 started on the same row.
// That roster is everything a client has between hands, and with no
// seat STATE on it the felt built its rows with a hard-coded "active" —
// so a player benched by the idle three-strike was drawn as playing and
// told the shuffle was about to start. It could not: the table was
// waiting for that same player to sit back in. Additive; a pre-bump
// server omits both and the client reads `false`, which is the value it
// was fabricating anyway. Both ends now report `(2, 43)`.
//
// Minor 44 adds `pending_sit_out` to `SeatOwnerSlot` — the flip the
// server has parked until the hand boundary. `start_eligible` reads
// that queue BEFORE the seat state, so a client holding only the state
// answers "can this table deal?" differently from the server that
// decides it: a seat that took its chair mid-hand is `sitting_out` with
// a sit-back queued, and the felt counted it as absent and told two
// ready players the table was waiting for somebody else. The flip is
// already broadcast as `seat_sit_out_flipped`, but an event is not
// state — a client that reloads after one lands never learns it, and a
// reconnect is when it matters most. Additive; the key absent means a
// pre-bump server (NOT the same as `null`, which is a minor-44 server
// saying nothing is queued for that seat) and the client keeps reading
// the seat state. Both ends now report `(2, 44)`.
//
// Minor 45 adds `PlayerLeftReason::Busted`. A seat that cannot cover
// the big blind is never dealt in, and nothing on the server moved it,
// so an out-of-chips player who stayed connected held the chair for
// good — heads-up, that stops the table outright. The server reclaims
// it after a grace now, and unlike a disconnect sweep the player is
// watching when it happens: the frame carries the reason so their
// client can say what took the seat rather than letting it vanish.
// Additive on an enum whose unknown arms were already tolerated. Both
// ends now report `(2, 45)`.
//
// Minor 46 adds `SeatRebought`. A top-up was durable the instant the
// `/rebuy` route returned and told nobody — so the caller's own felt
// kept the old stack until some later frame happened to carry a new
// one. In practice that meant the next deal: a player who added chips
// saw nothing happen for the length of the inter-hand pause and
// reasonably concluded the button had not worked. At a table too short
// of chips to deal — the case a top-up is FOR — nothing ever carried
// it. Additive END-variant; an older client ignores an unknown kind.
// Both ends now report `(2, 46)`.
//
// Minor 49 adds `expected_player_ids` to `seed_round_opened` and to
// the `seed_in_flight` view: the roster a seed round's commit
// attests. The server has always sealed that set when the round
// opened and refused every envelope from outside it, but the frame
// carried only its SIZE — so this client had nowhere to ask whether
// it was one of the players being asked, and answered from the only
// fact it had: that it held a seat. Those part ways the moment a
// player sits down while a round is open, which the server handles by
// parking them `SittingOut` for exactly this reason. Their
// contribution came back `403 seed.unknown_player` — a correct
// refusal this client could only render as a broken shuffle demanding
// a page reload. Additive field; an older server omits it and
// `seedRole` reads the absence as "not told" rather than "nobody".
// Both ends now report `(2, 49)`.
// Wire minor 50 adds `winners` to `settled` — who took chips from a
// CONTESTED pot. This client used to read the winner out of `awards`,
// crowning every seat with a positive amount; an uncalled bet coming
// back is a positive amount, so the player who bet 1,960 into an
// all-in call of 1,570, got 390 back and LOST the pot wore the crown
// beside the player who had won it. Nothing in `awards` or `invested`
// separates the two — a seat can win a side pot for less than it put
// in — so this end could never have got it right from what it had.
// The engine has always known; the answer now crosses the wire.
// Additive field; an older server omits it and the client reads the
// absence as "not told" and draws no crown, rather than guessing from
// the money again.
// Both ends now report `(2, 50)`.
//
// Minor 52 — `uncalled_returned`: an all-in nobody could fully call is
// handed back as the action closes into the run-out, before the board,
// instead of inside the settle's awards.
//
// Minor 53 — `SeatStartedView.waiting_first_hand`: a seat waiting for
// the big blind keeps saying so on every `hand_started` it sits through.
//
// Minor 54 — `SeatStartedView.pending_sit_out`: the flip still parked
// after the boundary, so a seat parked again there (an arrival held off
// the small blind or the button) is not drawn as sitting out.
export const WIRE_FORMAT_MINOR = 54;

/**
 * Build/protocol stamp this client reports to the server's
 * min-client-version gate (user-approved policy 1). Sent on every
 * authed HTTP request as the `x-client-version` header (see
 * `session/authed_request.ts`); the server rejects a stale client with
 * `426 Upgrade Required` (HTTP) or the upgrade-required WS close code
 * (`WS_CLOSE_UPGRADE_REQUIRED`).
 *
 * Format is the `"<major>.<minor>"` string the BE's
 * `client_version::parse_version_str` expects — the SAME `(major,
 * minor)` pair the WS handshake's `protocol_version` frame announces,
 * so the version stamp advances exactly when the wire contract does
 * and the gate compares against the one fact that already governs wire
 * compatibility. A pure-FE-only change (CSS / copy) does not bump
 * this; that is intentional — such a build is wire-compatible with the
 * prior gate and must NOT be force-upgraded.
 */
export const CLIENT_VERSION_STRING = `${WIRE_FORMAT_MAJOR}.${WIRE_FORMAT_MINOR}`;

/**
 * The minimum client `(major, minor)` both ends agree the server gate
 * accepts (coordinated with the BE agent: `MIN_CLIENT_MAJOR = 2`,
 * `MIN_CLIENT_MINOR = 0`). The server defaults its gate conservatively
 * to THIS pair or lower — never above the freshly-built client's
 * `(WIRE_FORMAT_MAJOR, WIRE_FORMAT_MINOR)` — so the very client shipped
 * alongside the gate always passes.
 *
 * The load-time guard below + the vitest assertion pin the safety
 * invariant `current >= agreed` (lexicographic on `(major, minor)`),
 * so a future bump that would lock out the current build trips an
 * error instead of shipping.
 */
export const AGREED_MIN_CLIENT_MAJOR = 2;
export const AGREED_MIN_CLIENT_MINOR = 0;

/**
 * WebSocket close code the server emits when it tears a socket down
 * because the client version is below the gate (user-approved policy
 * 1). Coordinated with the BE (`client_version::WS_CLOSE_UPGRADE_REQUIRED`)
 * — the app-private 4000–4999 range (RFC 6455 §7.4.2); `4001` is the
 * code the FE pivots on to render the upgrade state, DISTINCT from the
 * session-revoked / displaced close so the WS client never has to guess
 * why the socket closed.
 */
export const WS_CLOSE_UPGRADE_REQUIRED = 4001;

// Load-time safety net: the current build's reported version must
// never fall below the version the server gate accepts, or the very
// client shipped alongside the gate would lock itself out. Lexicographic
// `(major, minor)` compare — mirrors the BE `ClientVersion::meets_minimum`.
// Fires at module init (so a bad bump throws on the first import —
// including the vitest run + the dev/prod boot) instead of silently
// shipping a self-locking build. The vitest suite asserts the same.
const _currentMeetsAgreedMinimum =
  WIRE_FORMAT_MAJOR > AGREED_MIN_CLIENT_MAJOR ||
  (WIRE_FORMAT_MAJOR === AGREED_MIN_CLIENT_MAJOR &&
    WIRE_FORMAT_MINOR >= AGREED_MIN_CLIENT_MINOR);
if (!_currentMeetsAgreedMinimum) {
  throw new Error(
    `arkhe-holdem: current client version (${CLIENT_VERSION_STRING}) is below the ` +
      `agreed gate minimum (${AGREED_MIN_CLIENT_MAJOR}.${AGREED_MIN_CLIENT_MINOR}); ` +
      `the current build would be locked out by its own min-client-version gate.`,
  );
}

/** 32-byte post-reveal seed aggregate carried on `HandStarted` /
 *  `HandSnapshot`. Serialized as a JSON array of 32 ints by the server. */
export type FinalSeed = Uint8Array;

/** 16-byte opaque table identifier as the wire delivers it (lowercase
 *  hex string). */
export type TableId = string;

/** 32-byte ed25519 verifying key, hex-encoded on the wire. */
export type PlayerIdHex = string;

/**
 * The lane string carried on `auth_ok` — kept as a literal-union
 * alias so the WS client's lane assertion can compare safely
 * without pulling the `WsLane` symbol out of `src/ws/client.ts`
 * (which would create a circular import).
 */
export type WsLane = "player" | "spectator";

/**
 * WS-auth domain separator. Bound into the BLAKE3 challenge payload
 * prefix on both ends — backend `arkhe-holdem-server::ws_auth::
 * DOMAIN_WS_AUTH` mirrors this exact byte sequence. The `v1` suffix
 * pins the payload shape so a future widening (e.g. adding a
 * reconnect generation counter) can ship with a `v2` separator
 * without ambiguity.
 */
export const DOMAIN_WS_AUTH = new TextEncoder().encode(
  "arkhe-holdem::ws-auth::v1",
);

/**
 * HTTP-route auth domain separator (cryptographer Q4). Bound into
 * the BLAKE3 payload computed by `signPlayerAuthHeaders` — backend
 * `arkhe-holdem-server::route_auth::DOMAIN_PLAYER_AUTH` mirrors
 * the exact byte sequence. Same `v1` suffix convention as the WS
 * companion.
 */
export const DOMAIN_PLAYER_AUTH = new TextEncoder().encode(
  "arkhe-holdem::player-auth::v1",
);

/**
 * Account-self HTTP auth domain separator. Bound into the BLAKE3
 * payload computed by `signAccountSelfAuthHeaders` (the wallet /
 * RG / cash-mode self-polling routes that live outside any single
 * table). Backend `arkhe-holdem-server::auth::DOMAIN_ACCOUNT_SELF_AUTH`
 * mirrors the exact byte sequence — distinct from `DOMAIN_PLAYER_AUTH`
 * so a signature captured on a table-scoped surface cannot be
 * replayed against the owner-scoped self routes (and vice versa).
 */
export const DOMAIN_ACCOUNT_SELF_AUTH = new TextEncoder().encode(
  "arkhe-holdem::account-self::v1",
);
